import {
  Box,
  Disclaimer,
  Div,
  formatAmount,
  H5,
  InfoComponentMobile,
  lightTheme,
  NumberType,
  P,
  TableHeader,
} from '@vgrubs/components';

export type ServiceFeeRow = {
  key: string | number;
  name: string;
  /** A quieter line under the name (the partners behind a location). */
  hint?: string;
  onOpen?: () => void;
  card: number;
  cash: number;
  total: number;
  orders: number;
};

type Props = {
  /** Header of the first column: "Partner" or "Location". */
  nameLabel: string;
  rows: ServiceFeeRow[];
  emptyText: string;
};

const usd = (cents: number) => formatAmount(cents, NumberType.USD);

const WIDTHS = {
  name: '36%',
  orders: '13%',
  card: '15%',
  cash: '15%',
  total: '15%',
};

/**
 * One split of the service fee, a line per partner or per location, in the
 * order the backend sends (biggest total first).
 */
export const ServiceFeeTable = ({ nameLabel, rows, emptyText }: Props) => {
  if (rows.length === 0)
    return (
      <Div $flex $center $height="80px">
        <P $m="0" $color={lightTheme.lightGray}>
          {emptyText}
        </P>
      </Div>
    );

  const columns = [
    { label: nameLabel, width: WIDTHS.name },
    { label: 'Orders', width: WIDTHS.orders },
    { label: 'Card', width: WIDTHS.card },
    { label: 'Cash', width: WIDTHS.cash },
    { label: 'Total', width: WIDTHS.total },
  ];

  return (
    <>
      <TableHeader columns={columns} padding="0 16px" gap="12px" />
      {rows.map((row) => {
        const name = (
          <P
            $m="0"
            $pointer={!!row.onOpen}
            $hoverUnderline={!!row.onOpen}
            onClick={row.onOpen}
          >
            {row.name}
          </P>
        );

        return (
          <Div key={row.key}>
            <Div
              $desktop
              $flex
              $alignCenter
              $gap="12px"
              $p="12px 16px"
              $borderBottom
              $borderColor={lightTheme.mediumGray}
            >
              <Div $width={WIDTHS.name}>
                {name}
                {row.hint && (
                  <Disclaimer $m="0" $color={lightTheme.lightGray}>
                    {row.hint}
                  </Disclaimer>
                )}
              </Div>
              <Div $width={WIDTHS.orders}>
                <P $m="0">{row.orders}</P>
              </Div>
              <Div $width={WIDTHS.card}>
                <P $m="0">{usd(row.card)}</P>
              </Div>
              <Div $width={WIDTHS.cash}>
                <P $m="0">{usd(row.cash)}</P>
              </Div>
              <Div $width={WIDTHS.total}>
                <P $m="0" $color={lightTheme.green}>
                  {usd(row.total)}
                </P>
              </Div>
            </Div>

            <Box
              $mobile
              $flex
              $column
              $gap="4px"
              $mb="8"
              $p="12px 16px"
              $pSM="12px 16px"
              style={{ boxShadow: 'none' }}
            >
              <Div $flex $alignStart $between $gap="8px">
                <H5 $m="0" $pointer={!!row.onOpen} onClick={row.onOpen}>
                  {row.name}
                </H5>
                <H5 $m="0" $noWrap $color={lightTheme.green}>
                  {usd(row.total)}
                </H5>
              </Div>
              {row.hint && (
                <Disclaimer $m="0" $color={lightTheme.lightGray}>
                  {row.hint}
                </Disclaimer>
              )}
              <InfoComponentMobile
                title="Orders"
                component={<Disclaimer $m="0">{row.orders}</Disclaimer>}
              />
              <InfoComponentMobile
                title="Card"
                component={<Disclaimer $m="0">{usd(row.card)}</Disclaimer>}
              />
              <InfoComponentMobile
                title="Cash"
                component={<Disclaimer $m="0">{usd(row.cash)}</Disclaimer>}
              />
            </Box>
          </Div>
        );
      })}
    </>
  );
};
